import { useState } from 'react';
import { Image as ImageIcon, X, FileText } from 'lucide-react';
import { useAdminContent } from '@/admin/AdminContentContext';
import { LANG_META } from '@/admin/schema';
import { MediaPickerModal, mediaUrl } from '@/admin/media';
import { LANG_KEYS } from '@/lib/content';

export const inp = 'w-full rounded-xl border border-brand-line bg-white px-3.5 py-2.5 text-sm font-semibold text-brand-ink outline-none transition-all focus:border-brand-blue focus:ring-4 focus:ring-brand-blue/10';
const tid = (p) => p.replace(/\./g, '-');

function Label({ children }) { return <span className="mb-1.5 block text-xs font-bold text-brand-steel">{children}</span>; }

export function Toggle({ checked, onChange, testid, label }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${checked ? 'border-brand-emerald/40 bg-brand-emerald/10 text-emerald-700' : 'border-brand-line bg-white text-brand-dim'}`}>
      <span className={`relative h-4 w-7 rounded-full transition-colors ${checked ? 'bg-brand-emerald' : 'bg-brand-line'}`}>
        <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${checked ? 'start-3.5' : 'start-0.5'}`} />
      </span>
      {label ?? (checked ? 'ظاهر للزوار' : 'مخفي')}
    </button>
  );
}

function LocalizedInputs({ path, type }) {
  const { get, set } = useAdminContent();
  const lines = type === 'lines';
  const multi = type === 'textarea' || lines;
  return (
    <div className="grid gap-3 md:grid-cols-3">
      {LANG_KEYS.map((l) => {
        const raw = get(l, path);
        const val = lines ? (Array.isArray(raw) ? raw.join('\n') : raw || '') : raw ?? '';
        const onChange = (e) => set(l, path, lines ? e.target.value.split('\n') : e.target.value);
        const props = { value: typeof val === 'string' ? val : '', onChange, dir: LANG_META[l].dir, lang: l, className: inp, 'data-testid': `field-${tid(path)}-${l}` };
        return (
          <label key={l} className="block">
            <span className="mb-1 flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-brand-dim">{LANG_META[l].label}</span>
            {multi ? <textarea rows={lines ? 4 : 3} {...props} /> : <input {...props} />}
          </label>
        );
      })}
    </div>
  );
}

function SharedInput({ path, type, options }) {
  const { get, setShared } = useAdminContent();
  const val = get('ar', path) ?? get('en', path) ?? '';
  const inputType = type === 'number' ? 'number' : type === 'date' ? 'date' : 'text';
  if (type === 'select') {
    return (
      <select value={val} data-testid={`field-${tid(path)}`} onChange={(e) => setShared(path, e.target.value)} className={inp} dir="ltr">
        {(options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (type === 'sharedtext') {
    return <textarea rows={8} value={val} dir="auto" data-testid={`field-${tid(path)}`} onChange={(e) => setShared(path, e.target.value)} className={inp} />;
  }
  return (
    <input type={inputType} value={val} dir="ltr" data-testid={`field-${tid(path)}`}
      onChange={(e) => setShared(path, type === 'number' ? Number(e.target.value) : e.target.value)}
      placeholder={type === 'link' ? '/contact أو https://…' : ''} className={inp} />
  );
}

function ImageInput({ path }) {
  const { get, setShared } = useAdminContent();
  const [open, setOpen] = useState(false);
  const val = get('ar', path) ?? get('en', path) ?? '';
  const isImg = /\.(jpe?g|png|webp|svg|gif)(\?|$)/i.test(val) || (val && !/\.(pdf|apk)(\?|$)/i.test(val) && !val.startsWith('/pdf'));
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="grid h-16 w-24 shrink-0 place-items-center overflow-hidden rounded-xl border border-brand-line bg-brand-paper">
        {val ? (isImg ? <img src={mediaUrl(val)} alt="" className="h-full w-full object-cover" /> : <FileText className="h-6 w-6 text-brand-steel" />) : <ImageIcon className="h-5 w-5 text-brand-dim" />}
      </div>
      <input value={val} dir="ltr" onChange={(e) => setShared(path, e.target.value)} data-testid={`field-${tid(path)}`} placeholder="رابط الصورة/الملف أو اختر من المكتبة" className={`${inp} min-w-[200px] flex-1`} />
      <button type="button" onClick={() => setOpen(true)} data-testid={`pick-${tid(path)}`} className="rounded-full border border-brand-blue/30 bg-brand-blue/5 px-4 py-2 text-xs font-bold text-brand-blue hover:bg-brand-blue/10">اختيار من المكتبة</button>
      {val && <button type="button" onClick={() => setShared(path, '')} title="مسح" className="grid h-8 w-8 place-items-center rounded-full text-brand-dim hover:text-brand-crimson"><X className="h-4 w-4" /></button>}
      {open && <MediaPickerModal value={val} onClose={() => setOpen(false)} onPick={(m) => { setShared(path, m.url); setOpen(false); }} />}
    </div>
  );
}

export function FieldRow({ field, base = '' }) {
  const { get, setShared } = useAdminContent();
  const path = base ? `${base}.${field.path}` : field.path;
  let body;
  if (field.type === 'toggle') body = <Toggle checked={!!(get('ar', path) ?? get('en', path))} onChange={(v) => setShared(path, v)} testid={`field-${tid(path)}`} label={(get('ar', path) ?? get('en', path)) ? 'مفعّل' : 'غير مفعّل'} />;
  else if (field.type === 'image') body = <ImageInput path={path} />;
  else if (['shared', 'sharedtext', 'link', 'number', 'date', 'select'].includes(field.type)) body = <SharedInput path={path} type={field.type} options={field.options} />;
  else body = <LocalizedInputs path={path} type={field.type} />;
  return (
    <div data-testid={`row-${tid(path)}`}>
      <Label>{field.label}</Label>
      {body}
    </div>
  );
}
